import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Toast } from "react-vant";
import DDZWalletEntry from "./index";
import Mjh5GameEntry from "../Mjh5GameEntry";
import { PersistContext } from "@/data/PersistProvider";
import { LanguageContext } from "@/data/LanguageProvider";
import { useEnhancedHistory } from "@/data/RouterProvider";
import { ChannelRemind } from "@/utils/UrlHelper";
import { GetHallServerUrl } from "@/data/urls";
import { DDZ_INVITE_PATH, inviteFromSearch, inviteLocation, readInviteContext, inviteAuthReturnPath } from "@/utils/DDZInvite";
import { useDDZInviteBridge } from "@/utils/useDDZInviteBridge";
import { isMjh5Game, getMjh5CategoryGame, normalizeMjh5Entry, MJH5_LAUNCH } from "@/config/Mjh5";

export const isDDZEntry = isMjh5Game;

const EntryContext = createContext(null);
const showEntryError = language => Toast.fail(/^zh/i.test(language) ? "该游戏入口暂不可用，请重新选择。" :
  /^(id|in)/i.test(language) ? "Pilihan game ini belum tersedia. Silakan pilih lagi." :
    "This game entry is unavailable. Please choose again.");
const pendingKey = () => `ddz_invite_pending:${encodeURIComponent(GetHallServerUrl())}`;
function storedInvite(value) {
  try {
    const key = pendingKey();
    if (value === undefined) {
      const inviteToken = sessionStorage.getItem(key);
      if (inviteToken === null) return null;
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem(`${key}:context`) || "null"); } catch (_) { /* Legacy token-only storage. */ }
      return readInviteContext(inviteLocation(inviteToken,
        saved && saved.inviteToken === inviteToken ? saved.attribution : "").split("?")[1]);
    }
    if (value === null) {
      sessionStorage.removeItem(key);
      sessionStorage.removeItem(`${key}:context`);
    } else {
      sessionStorage.setItem(key, value.inviteToken);
      sessionStorage.setItem(`${key}:context`, JSON.stringify(value));
    }
  } catch (_) { /* The login return URL also preserves the invitation. */ }
  return null;
}

export function DDZWalletProvider({ children }) {
  const { getPersist, setPersist, reloadUserInfo } = useContext(PersistContext);
  const { getLanguage } = useContext(LanguageContext);
  const history = useEnhancedHistory();
  const location = useLocation();
  const loaded = getPersist("dataLoaded");
  const user = getPersist("user_info");
  const token = user && !user.is_guest ? user.token : null;
  const account = user && user.rid;
  const [entry, setEntry] = useState(null);
  const [choice, setChoice] = useState(null);
  const choiceRef = useRef(null);
  // Tickets stay in memory until the existing Home iframe is ready.
  const [launch, setLaunch] = useState(null);
  const [gameActive, setGameActive] = useState(false);
  const launcher = useRef(null);
  const [launcherVersion, setLauncherVersion] = useState(0);
  const revision = useRef(0);
  const handled = useRef("");
  const actions = useRef({ history, setPersist, getPersist, reloadUserInfo, getLanguage });
  actions.current = { history, setPersist, getPersist, reloadUserInfo, getLanguage };
  const pending = getPersist("ddz_wallet_open");
  const nativeInvite = useDDZInviteBridge({ location, history, loaded,
    busy: gameActive || !!entry || !!launch || !!choice, getLanguage });
  const gameClosed = useCallback(() => setGameActive(false), []);
  const gameOpened = useCallback(() => setGameActive(true), []);

  useEffect(() => {
    setEntry(current => current && (current.token !== token || current.account !== account) ? null : current);
  }, [token, account]);

  useEffect(() => {
    choiceRef.current = null;
    setChoice(null);
  }, [token, account, location.pathname, location.search]);

  const registerLauncher = useCallback(handler => {
    const registration = { handler };
    launcher.current = registration;
    setLauncherVersion(value => value + 1);
    return () => {
      if (launcher.current === registration) { launcher.current = null; setGameActive(false); }
    };
  }, []);

  useEffect(() => {
    if (!loaded) return;
    // A newer native delivery can replace a URL staged by the bridge before this effect runs.
    // Direct links and an already open wallet retain their existing ownership.
    if (nativeInvite.superseded) return;
    const { history, setPersist } = actions.current;
    let context = readInviteContext(location.search);
    const authPage = /^\/(login|sendRegCode)$/.test(location.pathname);
    if (context === null && location.pathname === DDZ_INVITE_PATH) context = storedInvite() || { inviteToken: "", attribution: {} };
    if (context === null && authPage && !token) {
      const query = new URLSearchParams(location.search);
      const rawBackTo = query.has("back_to") ? query.get("back_to") : actions.current.getPersist("login_back_url");
      const backTo = inviteAuthReturnPath(rawBackTo);
      let explicitInvite = false;
      try { explicitInvite = !!rawBackTo && new URL(rawBackTo, window.location.origin).searchParams.has("invite_token"); } catch (_) {}
      // A new ordinary login destination owns navigation. A leftover invitation
      // must not replace a deposit/login return or its registration attribution.
      if (rawBackTo !== null && rawBackTo !== undefined && !backTo && !explicitInvite) {
        const abandoned = storedInvite();
        if (abandoned) nativeInvite.finish(abandoned.inviteToken, inviteLocation(abandoned.inviteToken, abandoned.attribution));
        storedInvite(null);
        return;
      }
      const saved = backTo ? readInviteContext(backTo.split("?")[1]) : explicitInvite ?
        { inviteToken: "", attribution: {} } : storedInvite();
      if (saved !== null) {
        storedInvite(saved);
        setPersist("login_back_url", inviteLocation(saved.inviteToken, saved.attribution));
        setPersist("agent_rid", saved.attribution.aid || "");
        setPersist("agent_from", saved.attribution.f || "");
        if (saved.attribution.c) setPersist("channel", saved.attribution.c);
      }
      return;
    }
    if (context !== null) {
      const { inviteToken, attribution } = context;
      const invitePath = inviteLocation(inviteToken, attribution);
      storedInvite(context);
      setPersist("ddz_wallet_open", false);
      setPersist("ddz_wallet_target", null);
      setPersist("mjh5_pending_entry", null);
      setLaunch(null);
      if (!token) {
        setEntry(null);
        setPersist("login_back_url", invitePath);
        setPersist("agent_rid", attribution.aid || "");
        setPersist("agent_from", attribution.f || "");
        if (attribution.c) setPersist("channel", attribution.c);
        if (attribution.p) setPersist("platform", attribution.p);
        if (!authPage) {
          const query = new URLSearchParams({ reg: user ? "2" : "1", back_to: invitePath });
          if (attribution.c) query.set("c", attribution.c);
          history.replace(`/login?${query}`);
        }
        else if (inviteFromSearch(location.search) !== null) {
          const query = new URLSearchParams(location.search);
          query.delete("invite_token");
          query.set("back_to", invitePath);
          history.replace(`${location.pathname}${query.toString() ? `?${query}` : ""}`);
        }
        return;
      }
      if (authPage) {
        history.replace(invitePath);
        return;
      }
      const key = `${token}:${account}:${location.key}:${location.pathname}:${location.search}`;
      if (handled.current === key) return;
      handled.current = key;
      setEntry({ token, account, inviteToken, invitePath, entry: "lobby", revision: ++revision.current });
    } else if (token && pending && !authPage) {
      const target = normalizeMjh5Entry(actions.current.getPersist("ddz_wallet_target"));
      setPersist("ddz_wallet_open", false);
      setPersist("ddz_wallet_target", null);
      if (!target) {
        showEntryError(actions.current.getLanguage());
        return;
      }
      setEntry({ token, account, inviteToken: null, entry: target, revision: ++revision.current });
    }
  }, [loaded, user, token, account, pending, location.key, location.pathname, location.search, nativeInvite.superseded]);

  useEffect(() => {
    if (!launch) return;
    const current = actions.current.getPersist("user_info");
    if (!token || launch.token !== token || launch.account !== account ||
        !current || current.is_guest || current.token !== token || current.rid !== account ||
        inviteFromSearch(window.location.search) !== null) {
      setLaunch(null);
      return;
    }
    if (launcher.current) {
      setLaunch(null);
      let accepted = false;
      try { accepted = launcher.current.handler(launch) !== false; } catch (_) { /* Reopen the wallet below. */ }
      if (accepted) setGameActive(true);
      if (!accepted) setEntry({ token, account, inviteToken: null, entry: launch.entry, revision: ++revision.current });
    } else if (location.pathname !== "/home" && location.pathname !== "/") {
      actions.current.history.replace(ChannelRemind("/home"));
    }
  }, [launch, token, account, launcherVersion, location.pathname]);

  const clearInvite = () => {
    if (entry && entry.inviteToken !== null) nativeInvite.finish(entry.inviteToken, entry.invitePath);
    storedInvite(null);
    if (inviteAuthReturnPath(getPersist("login_back_url"))) setPersist("login_back_url", null);
    const query = new URLSearchParams(location.search);
    if (query.has("invite_token")) ["aid", "f", "p"].forEach(key => query.delete(key));
    query.delete("invite_token");
    const path = location.pathname === DDZ_INVITE_PATH ? "/home" : location.pathname;
    if (path !== location.pathname || query.toString() !== location.search.replace(/^\?/, "")) {
      history.replace(`${path}${query.toString() ? `?${query}` : ""}`);
    }
  };
  const open = (game, target = "lobby") => {
    if (!isDDZEntry(game)) return false;
    target = normalizeMjh5Entry(target);
    if (!target) { showEntryError(getLanguage()); return false; }
    choiceRef.current = null;
    setChoice(null);
    clearInvite();
    setPersist("mjh5_pending_entry", null);
    setLaunch(null);
    if (!token) {
      setPersist("ddz_wallet_open", true);
      setPersist("ddz_wallet_target", target);
      setPersist("login_back_url", ChannelRemind("/home"));
      history.push(ChannelRemind(user ? "/login?reg=2" : "/login"));
    } else {
      setPersist("ddz_wallet_open", false);
      setPersist("ddz_wallet_target", null);
      setEntry({ token, account, inviteToken: null, entry: target, revision: ++revision.current });
    }
    return true;
  };
  const restore = previous => {
    const current = getPersist("user_info");
    if (!previous || !token || !current || current.is_guest || current.token !== previous.token ||
        current.rid !== previous.account || token !== previous.token) return false;
    const target = normalizeMjh5Entry(previous.entry);
    if (!target) { showEntryError(getLanguage()); return false; }
    setGameActive(false);
    clearInvite();
    setLaunch(null);
    setEntry({ token, account, inviteToken: null, entry: target, revision: ++revision.current });
    return true;
  };
  const isOpen = !!(token && entry && entry.token === token && entry.account === account);
  const openGame = game => {
    const target = getMjh5CategoryGame(game);
    if (isOpen || launch || choiceRef.current) return false;
    if (!target) { showEntryError(getLanguage()); return false; }
    choiceRef.current = target;
    setChoice(target);
    return true;
  };
  const closeChoice = () => {
    choiceRef.current = null;
    setChoice(null);
  };
  const selectChoice = target => {
    const current = choiceRef.current;
    const selected = target && normalizeMjh5Entry(target);
    if (!current || !selected || ![current.matchEntry, current.createEntry].includes(selected)) return;
    open({ game_uid: MJH5_LAUNCH.gameUid }, selected);
  };
  const modal = isOpen ? <DDZWalletEntry
    key={`${token}:${entry.revision}`} token={token} account={account} language={getLanguage()}
    entry={entry.entry} inviteToken={entry.inviteToken} autoLaunch={entry.inviteToken !== null} onClose={() => {
      if (entry.revision !== revision.current) return;
      setEntry(null); clearInvite();
    }}
    onTransferred={() => {
      const current = getPersist("user_info");
      if (current && !current.is_guest && current.token === token && current.rid === account) reloadUserInfo(token);
    }}
    onLaunch={url => {
      const current = getPersist("user_info");
      if (!current || current.is_guest || current.token !== token || current.rid !== account || entry.revision !== revision.current) return;
      clearInvite();
      setEntry(null);
      setLaunch({ url, token, account, entry: entry.entry });
    }}
  /> : null;
  return <EntryContext.Provider value={{ open, openGame, modal: null, isOpen: isOpen || !!choice, isLaunching: !!launch, registerLauncher, restore, gameClosed, gameOpened }}>
    {children}{modal}
    {choice && <Mjh5GameEntry game={choice} language={getLanguage()} onSelect={selectChoice} onClose={closeChoice} />}
  </EntryContext.Provider>;
}

export default function useDDZWalletEntry() {
  return useContext(EntryContext);
}
